import { t } from '@avalara/bat-apps-shared/i18n';
import { SCol, SIcon, SRow } from '@avalara/skylab-sdk/react';
import type React from 'react';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { RegionModel } from '../../../../types/definitions';
import { getRegions } from '../../lib/side-effects/avatax';
import { selectState } from '../../selectors';
import { convertCountryNameToCode, getValidationMessage, zuoraRegions } from '../helpers/contact-info';
import ContactInfoField from './contactInfoField';
import type { ContactInfoProps } from './index';

const Address = ({
  billingContactEditForm,
  isAdmin,
  onUpdateFormField,
  showValidationErrors,
  validationErrors,
}: ContactInfoProps) => {
  const dispatch = useDispatch();
  const {
    data: { countries, regions },
  } = useSelector(selectState);
  const { Country, State } = billingContactEditForm;

  const [regionOptions, setRegionOptions] = useState<Array<RegionModel>>([]);
  const countryCode = convertCountryNameToCode(countries, Country);

  const isSpecialZuoraCountry = countryCode === 'US' || countryCode === 'CA';

  useEffect(() => {
    if (isSpecialZuoraCountry) {
      setRegionOptions(zuoraRegions[countryCode].map((s) => ({ code: s, name: s })));
    } else if (countryCode) {
      setRegionOptions([]);
      dispatch(getRegions(countryCode));
    }
  }, [countryCode, isSpecialZuoraCountry]);

  useEffect(() => {
    if (countryCode && !isSpecialZuoraCountry) setRegionOptions(regions[countryCode] || []);
  }, [countryCode, regions, isSpecialZuoraCountry]);

  const countryValidationErr = getValidationMessage(validationErrors.Country, 'Country', 'country');
  const stateValidationErr = getValidationMessage(validationErrors.State, 'State', 'state');

  const countryId = 'contact-info-field-country';
  const countryMessageId = `${countryId}-message`;
  const regionId = 'contact-info-field-region';
  const regionMessageId = `${regionId}-message`;

  const showCountryError = showValidationErrors && countryValidationErr != null;
  const showRegionError = showValidationErrors && stateValidationErr != null;

  return (
    <>
      <h3 className='margin-left-sm margin-bottom-xs' data-ref='buydot.contact-info.billing-address-header'>
        {t('client.headings.billing-address')}
      </h3>
      <SRow class='pad-bottom-none'>
        <SCol span='6' class='pad-bottom-sm'>
          <label
            className='flex margin-top-sm required'
            data-ref='buydot.contact-info.address.country-label'
            htmlFor={countryId}
          >
            {t('client.labels.country')}
          </label>
          <select
            id={countryId}
            data-ref='buydot.contact-info.address.country-select'
            aria-label='country'
            disabled={!isAdmin}
            className={showCountryError ? 'error' : ''}
            aria-invalid={showCountryError ? 'true' : 'false'}
            aria-describedby={showCountryError ? countryMessageId : undefined}
            aria-required='true'
            value={countryCode || ''}
            onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
              if (e.target.value === countryCode) return;
              onUpdateFormField('State', null);
              onUpdateFormField('Country', e.target.value);
            }}
          >
            <option value='' disabled>
              {t('client.general.select-one')}
            </option>
            {countries.map((country) => (
              <option value={country.code} key={country.code}>
                {country.name}
              </option>
            ))}
          </select>
          <div id={countryMessageId} className='input-msg'>
            <SIcon name='alert-circle-filled' aria-hidden='true' />
            {countryValidationErr}
          </div>
        </SCol>
      </SRow>
      <SRow class='pad-bottom-none'>
        <SCol span='6' class='pad-bottom-sm'>
          <ContactInfoField
            disabled={!isAdmin}
            displayName='Address 1'
            fieldName='Address1'
            fieldValue={billingContactEditForm.Address1}
            required={true}
            showValidationErrors={showValidationErrors}
            onUpdateFormField={onUpdateFormField}
            validationError={validationErrors.Address1}
          />
        </SCol>
      </SRow>
      <SRow class='pad-bottom-none'>
        <SCol span='6' class='pad-bottom-sm'>
          <ContactInfoField
            disabled={!isAdmin}
            displayName='Address 2'
            fieldName='Address2'
            fieldValue={billingContactEditForm.Address2}
            required={false}
            showValidationErrors={showValidationErrors}
            onUpdateFormField={onUpdateFormField}
            validationError={null}
          />
        </SCol>
      </SRow>
      <SRow class='pad-bottom-none'>
        <SCol span='6' class='pad-bottom-sm'>
          <ContactInfoField
            disabled={!isAdmin}
            displayName={t('client.labels.city')}
            fieldName='City'
            fieldValue={billingContactEditForm.City}
            required={true}
            showValidationErrors={showValidationErrors}
            onUpdateFormField={onUpdateFormField}
            validationError={validationErrors.City}
          />
        </SCol>
      </SRow>
      <SRow class='pad-bottom-none'>
        <SCol span='6' class='pad-bottom-sm'>
          <label className='flex margin-top-sm required' htmlFor={regionId}>
            {t('client.labels.region')}
          </label>
          <select
            id={regionId}
            data-ref='buydot.address.region-select'
            aria-label='region'
            disabled={!isAdmin}
            className={showRegionError ? 'error' : ''}
            aria-invalid={showRegionError ? 'true' : 'false'}
            aria-describedby={showRegionError ? regionMessageId : undefined}
            aria-required='true'
            value={State || ''}
            onChange={(e) => onUpdateFormField('State', e.target.value)}
          >
            <option value='' disabled>
              {t('client.general.select-one')}
            </option>
            {regionOptions.map((region) => (
              <option value={region.name} key={region.code}>
                {region.name}
              </option>
            ))}
          </select>
          <div id={regionMessageId} className='input-msg'>
            <SIcon name='alert-circle-filled' aria-hidden='true' />
            {stateValidationErr}
          </div>
        </SCol>
      </SRow>
      <SRow class='pad-bottom-none'>
        <SCol span='6' class='pad-bottom-sm'>
          <ContactInfoField
            disabled={!isAdmin}
            displayName='Postal Code'
            fieldName='PostalCode'
            fieldValue={billingContactEditForm.PostalCode}
            required={true}
            showValidationErrors={showValidationErrors}
            onUpdateFormField={onUpdateFormField}
            validationError={validationErrors.PostalCode}
            validationName='ZIP code'
          />
        </SCol>
      </SRow>
    </>
  );
};

export default Address;
